import React from 'react';
import { DailyMetrics } from '../../types';
import { parseLocalDate } from '../../utils/dateUtils';
import CoachCommentThread from '../coach/CoachCommentThread';
import CheckInPhotoSection from './CheckInPhotoSection';
import { useCoachView } from '../../contexts/CoachViewContext';
import {
  useAlertThresholds,
  evaluateMetric,
  evaluateBP,
  AlertLevel,
} from '../../hooks/useAlertThresholds';

interface Props {
  metric: DailyMetrics | null;
  onClose: () => void;
}

// ── Alert level → Tailwind text colour ───────────────────────────────────────

const ALERT_TEXT: Record<NonNullable<AlertLevel>, string> = {
  red:    'text-red-400 font-semibold',
  orange: 'text-orange-400 font-semibold',
  green:  'text-white',
};

function alertTextClass(level: AlertLevel): string {
  return level ? ALERT_TEXT[level] : 'text-white';
}

// ── Categorical alert helpers (non-numeric, hardcoded rules) ─────────────────

/** PED side effects: "Yes" is always clinically significant (red). */
function pedSeLevel(value?: boolean | null): AlertLevel {
  if (value == null) return null;
  return value ? 'red' : null;
}

/** Bowel movement consistency: Liquid → red, Loose → orange, Normal → green. */
function bmConsistencyLevel(consistency?: string | null): AlertLevel {
  if (!consistency) return null;
  if (consistency === 'Liquid') return 'red';
  if (consistency === 'Loose')  return 'orange';
  return 'green';
}

// ── Small read-only display helpers ──────────────────────────────────────────

const Field: React.FC<{
  label: string;
  value: React.ReactNode;
  alertLevel?: AlertLevel;
}> = ({ label, value, alertLevel }) => (
  <div>
    <p className="text-xs text-gray-500 mb-0.5">{label}</p>
    <p className={`text-sm ${alertLevel ? alertTextClass(alertLevel) : 'text-white'}`}>
      {value ?? <span className="text-gray-600 italic">—</span>}
    </p>
  </div>
);

const SectionHeader: React.FC<{ label: string }> = ({ label }) => (
  <div className="flex items-center gap-3 mb-3 mt-5 first:mt-0">
    <h3 className="text-xs font-semibold uppercase tracking-widest text-gray-500 whitespace-nowrap">{label}</h3>
    <div className="flex-1 border-t border-dark-700" />
  </div>
);

/** 1-10 tap-pip row — active pip coloured by alertLevel when provided. */
const ScoreDisplay: React.FC<{
  value?: number | null;
  low: string;
  high: string;
  alertLevel?: AlertLevel;
}> = ({ value, low, high, alertLevel }) => {
  if (value == null) return <span className="text-gray-600 italic">—</span>;

  const activePipClass =
    alertLevel === 'red'    ? 'bg-red-500 text-white' :
    alertLevel === 'orange' ? 'bg-orange-500 text-white' :
    'bg-gold-500 text-black';

  return (
    <div className="flex items-center gap-2">
      <span className="text-xs text-gray-500">{low}</span>
      <div className="flex gap-1">
        {[1,2,3,4,5,6,7,8,9,10].map((n) => (
          <div
            key={n}
            className={`w-6 h-6 rounded text-xs font-semibold flex items-center justify-center ${
              n === value ? activePipClass : 'bg-dark-700 text-gray-600'
            }`}
          >
            {n}
          </div>
        ))}
      </div>
      <span className="text-xs text-gray-500">{high}</span>
    </div>
  );
};

const WellbeingBadge: React.FC<{ value?: number | null }> = ({ value }) => {
  if (value == null) return <span className="text-gray-600 italic">—</span>;
  const cls =
    value >= 8 ? 'bg-green-900 text-green-200' :
    value >= 6 ? 'bg-yellow-900 text-yellow-200' :
    'bg-red-900 text-red-200';
  return (
    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${cls}`}>
      {value}/10
    </span>
  );
};

const YesNoBadge: React.FC<{ value?: boolean | null }> = ({ value }) => {
  if (value == null) return <span className="text-gray-600 italic">—</span>;
  return (
    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
      value ? 'bg-green-900 text-green-200' : 'bg-dark-700 text-gray-400'
    }`}>
      {value ? 'Yes' : 'No'}
    </span>
  );
};

/** PED side-effects badge: Yes → red (always clinically notable), No → neutral. */
const PedSideEffectsBadge: React.FC<{ value?: boolean | null }> = ({ value }) => {
  if (value == null) return <span className="text-gray-600 italic">—</span>;
  return (
    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
      value ? 'bg-red-900 text-red-200' : 'bg-dark-700 text-gray-400'
    }`}>
      {value ? 'Yes ⚠️' : 'No'}
    </span>
  );
};

/** Bowel movement consistency with colour-coding. */
const BmConsistencyBadge: React.FC<{ value?: string | null }> = ({ value }) => {
  if (!value) return null;
  const level = bmConsistencyLevel(value);
  const cls =
    level === 'red'    ? 'bg-red-900 text-red-200' :
    level === 'orange' ? 'bg-orange-900 text-orange-200' :
    'bg-dark-700 text-gray-300';
  return (
    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${cls}`}>
      {value}
    </span>
  );
};

// ── Main drawer ───────────────────────────────────────────────────────────────

const MetricsDetailDrawer: React.FC<Props> = ({ metric, onClose }) => {
  const { viewAs } = useCoachView();
  const { thresholds } = useAlertThresholds();

  if (!metric) return null;

  // Pre-compute alert levels for each flagged metric
  const bpLevel        = evaluateBP(metric.bloodPressureSystolic, metric.bloodPressureDiastolic, thresholds);
  const rhrLevel       = evaluateMetric('resting_hr',    metric.overnightRestingHeartRate, thresholds);
  const gluLevel       = evaluateMetric('glucose',        metric.glucoseLevel,              thresholds);
  const sleepHrsLevel  = evaluateMetric('sleep_hours',    metric.sleepHours,                thresholds);
  const sleepQualLevel = evaluateMetric('sleep_quality',  metric.sleepQuality,              thresholds);
  const wellbeingLevel = evaluateMetric('wellbeing',      metric.wellbeingScore,            thresholds);

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/60 z-40"
        onClick={onClose}
      />

      {/* Drawer panel */}
      <div className="fixed right-0 top-0 h-full w-full max-w-lg bg-dark-800 border-l border-dark-700 z-50 flex flex-col shadow-2xl">

        {/* Drawer header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-dark-700 bg-dark-900 flex-shrink-0">
          <div>
            <h2 className="text-base font-semibold text-gold-500">Check-In Details</h2>
            <p className="text-xs text-gray-400 mt-0.5">
              {parseLocalDate(metric.date).toLocaleDateString(undefined, {
                weekday: 'long', year: 'numeric', month: 'long', day: 'numeric'
              })}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white p-1 rounded-lg hover:bg-dark-700 transition-colors"
            aria-label="Close"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Scrollable content */}
        <div className="flex-1 overflow-y-auto px-6 py-4">

          {/* ── Body & Activity ── */}
          <SectionHeader label="Body & Activity" />
          <div className="grid grid-cols-2 gap-4">
            <Field
              label="Morning Weight"
              value={metric.weight != null ? `${metric.weight} lbs` : null}
            />
            <Field
              label="Steps (Yesterday)"
              value={metric.stepsYesterday != null ? metric.stepsYesterday.toLocaleString() : null}
            />
          </div>

          {/* ── Sleep & Recovery ── */}
          <SectionHeader label="Sleep & Recovery" />
          <div className="grid grid-cols-3 gap-4 mb-3">
            <Field
              label="Sleep"
              value={metric.sleepHours != null ? `${metric.sleepHours} hrs` : null}
              alertLevel={sleepHrsLevel}
            />
            <Field
              label="Overnight HRV"
              value={metric.overnightHrv != null ? `${metric.overnightHrv} ms` : null}
            />
            <Field
              label="Resting HR"
              value={metric.overnightRestingHeartRate != null ? `${metric.overnightRestingHeartRate} bpm` : null}
              alertLevel={rhrLevel}
            />
          </div>
          <div>
            <p className="text-xs text-gray-500 mb-1">Sleep Quality</p>
            <ScoreDisplay
              value={metric.sleepQuality}
              low="😴 poor"
              high="🌟 great"
              alertLevel={sleepQualLevel}
            />
          </div>

          {/* ── Vitals ── */}
          <SectionHeader label="Vitals" />
          <div className="grid grid-cols-3 gap-4">
            <Field
              label="Blood Pressure"
              value={
                metric.bloodPressureSystolic != null && metric.bloodPressureDiastolic != null
                  ? `${metric.bloodPressureSystolic} / ${metric.bloodPressureDiastolic}`
                  : null
              }
              alertLevel={bpLevel}
            />
            <Field
              label="Glucose"
              value={metric.glucoseLevel != null ? `${metric.glucoseLevel} mg/dL` : null}
              alertLevel={gluLevel}
            />
          </div>

          {/* ── Wellbeing ── */}
          <SectionHeader label="Wellbeing" />
          <div>
            <p className="text-xs text-gray-500 mb-1">Score</p>
            <ScoreDisplay
              value={metric.wellbeingScore}
              low="😔 poor"
              high="💪 great"
              alertLevel={wellbeingLevel}
            />
          </div>

          {/* ── Bowel Movement ── */}
          <SectionHeader label="Bowel Movement (Yesterday)" />
          <div className="flex items-center gap-4">
            <YesNoBadge value={metric.hadBowelMovement} />
            {metric.hadBowelMovement === true && metric.bowelMovementConsistency && (
              <BmConsistencyBadge value={metric.bowelMovementConsistency} />
            )}
          </div>

          {/* ── PED Side Effects ── */}
          <SectionHeader label="PED Side Effects" />
          <div>
            <PedSideEffectsBadge value={metric.pedSideEffects} />
            {metric.pedSideEffects === true && metric.pedSideEffectsNotes && (
              <p className="mt-2 text-sm text-gray-300 bg-dark-700 rounded-lg px-3 py-2 border border-dark-600 whitespace-pre-wrap">
                {metric.pedSideEffectsNotes}
              </p>
            )}
          </div>

          {/* ── Notes ── */}
          {metric.notes && (
            <>
              <SectionHeader label="Notes" />
              <p className="text-sm text-gray-300 bg-dark-700 rounded-lg px-3 py-2 border border-dark-600 whitespace-pre-wrap">
                {metric.notes}
              </p>
            </>
          )}

          {/* ── Check-In Photos — read-only coach view ── */}
          <CheckInPhotoSection
            metricId={metric.metricId}
            viewAs={viewAs ?? undefined}
            readOnly={true}
          />

          {/* ── Coach Notes ── */}
          <div className="mt-6">
            <CoachCommentThread
              contextType="DailyCheckIn"
              contextId={metric.metricId}
              label="check-in"
              defaultExpanded={true}
            />
          </div>

        </div>
      </div>
    </>
  );
};

export default MetricsDetailDrawer;
